import { AlertCircle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type {
  ConfidenceLevelApi,
  ForecastHorizonApi,
  ThemeApi,
} from "@/api/models/userPreferenceDefaults";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/contexts/AppContext";
import { useTerminology } from "@/hooks/useTerminology";
import { useUserPreferenceDefaults } from "@/hooks/useUserPreferenceDefaults";

interface PreferenceSection {
  id: string;
  name: string;
  settings: Setting[];
}

interface Setting {
  id: string;
  label: string;
  type: "toggle" | "amount" | "percentage" | "select";
  value: boolean | string | number;
  options?: { value: string; label: string }[];
  helperText?: string;
}

export function UserPreferenceDefaults() {
  const { getScenarioTerm, getForecastTerm } = useTerminology();
  const { mode } = useApp();

  const {
    prefs,
    isLoading,
    error,
    setLowCashBalanceThreshold,
    setOverdueInvoiceAlert,
    setVarianceAlertThreshold,
    setEmailNotifications,
    setFeedNotifications,
    setForecastHorizon,
    setConfidenceLevel,
    setIncludeWeekends,
    setTheme,
    save,
    isSaving,
  } = useUserPreferenceDefaults();

  // Local state only used in demo mode
  const [demoLowCashThreshold, setDemoLowCashThreshold] =
    useState<number>(50000);
  const [demoOverdueInvoiceAlert, setDemoOverdueInvoiceAlert] =
    useState<boolean>(true);
  const [demoVarianceThreshold, setDemoVarianceThreshold] =
    useState<number>(10);
  const [demoEmailNotifications, setDemoEmailNotifications] =
    useState<boolean>(true);
  const [demoFeedNotifications, setDemoFeedNotifications] =
    useState<boolean>(true);
  const [demoForecastHorizon, setDemoForecastHorizon] =
    useState<string>("90-days");
  const [demoConfidenceLevel, setDemoConfidenceLevel] =
    useState<string>("medium");
  const [demoIncludeWeekends, setDemoIncludeWeekends] =
    useState<boolean>(false);
  const [demoTheme, setDemoTheme] = useState<string>("light");
  const [demoDefaultLandingPage, setDemoDefaultLandingPage] =
    useState<string>("dashboard");
  const [demoIsSaving, setDemoIsSaving] = useState(false);

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set([
      "forecast-alerts",
      "transaction-alerts",
      "variance-alerts",
      "notifications",
      "forecasting",
      "display",
    ]),
  );

  const isLive = mode === "live";

  const sections: PreferenceSection[] = [
    {
      id: "forecast-alerts",
      name: "Forecast Alerts",
      settings: [
        {
          id: "low-cash-threshold",
          label: "Low Cash Balance Threshold",
          type: "amount",
          value: isLive ? prefs.lowCashBalanceThreshold : demoLowCashThreshold,
        },
      ],
    },
    {
      id: "transaction-alerts",
      name: "Transaction Alerts",
      settings: [
        {
          id: "overdue-invoice-alert",
          label: "Overdue Invoice Alert",
          type: "toggle",
          value: isLive ? prefs.overdueInvoiceAlert : demoOverdueInvoiceAlert,
        },
      ],
    },
    {
      id: "variance-alerts",
      name: "Variance Alerts",
      settings: [
        {
          id: "variance-threshold",
          label: "Variance Alert Threshold",
          type: "percentage",
          value: isLive ? prefs.varianceAlertThreshold : demoVarianceThreshold,
        },
      ],
    },
    {
      id: "notifications",
      name: "Notifications",
      settings: [
        {
          id: "email-notifications",
          label: "Email Notifications",
          type: "toggle",
          value: isLive ? prefs.emailNotifications : demoEmailNotifications,
        },
        {
          id: "feed-notifications",
          label: "Feed Notifications",
          type: "toggle",
          value: isLive ? prefs.feedNotifications : demoFeedNotifications,
        },
      ],
    },
    {
      id: "forecasting",
      name: "Forecasting",
      settings: [
        {
          id: "forecast-horizon",
          label: "Default 90-Day View",
          type: "select",
          value: isLive ? prefs.forecastHorizon : demoForecastHorizon,
          options: [
            { value: "7-days", label: "7 Days" },
            { value: "30-days", label: "30 Days" },
            { value: "90-days", label: "90 Days" },
          ],
          helperText:
            "Which period is pre-selected when you open the 90-Day forecast",
        },
        {
          id: "confidence-level",
          label: "Confidence Level",
          type: "select",
          value: isLive ? prefs.confidenceLevel : demoConfidenceLevel,
          options: prefs.confidenceLevelOptions.map((o) => ({
            value: o.value,
            label: o.label,
          })),
        },
        {
          id: "include-weekends",
          label: "Include Weekends",
          type: "toggle",
          value: isLive ? prefs.includeWeekends : demoIncludeWeekends,
        },
      ],
    },
    {
      id: "display",
      name: "Display",
      settings: [
        {
          id: "theme",
          label: "Theme",
          type: "select",
          value: isLive ? prefs.theme : demoTheme,
          options: prefs.themeOptions.map((o) => ({
            value: o.value,
            label: o.label,
          })),
        },
        {
          id: "default-landing-page",
          label: "Default Landing Page",
          type: "select",
          value: isLive
            ? (((prefs as unknown as Record<string, unknown>)
                .defaultLandingPage as string | undefined) ?? "dashboard")
            : demoDefaultLandingPage,
          options: [
            { value: "dashboard", label: "Dashboard" },
            {
              value: "forecast-90",
              label: `${getForecastTerm(true)}s — 90-Day View`,
            },
            {
              value: "forecast-12m",
              label: `${getForecastTerm(true)}s — 12-Month View`,
            },
            {
              value: "forecast-3y",
              label: `${getForecastTerm(true)}s — 3-Year View`,
            },
            { value: "transactions", label: "Transactions" },
            { value: "scenarios", label: getScenarioTerm() },
            { value: "reports", label: "Reports" },
          ],
        },
      ],
    },
  ];

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getEnabledCount = (section: PreferenceSection): number => {
    return section.settings.filter((s) => {
      if (s.type === "toggle") return s.value === true;
      return true;
    }).length;
  };

  const handleSettingChange = (
    settingId: string,
    value: boolean | string | number,
  ) => {
    if (isLive) {
      switch (settingId) {
        case "low-cash-threshold":
          setLowCashBalanceThreshold(Number(value));
          break;
        case "overdue-invoice-alert":
          setOverdueInvoiceAlert(value as boolean);
          break;
        case "variance-threshold":
          setVarianceAlertThreshold(Number(value));
          break;
        case "email-notifications":
          setEmailNotifications(value as boolean);
          break;
        case "feed-notifications":
          setFeedNotifications(value as boolean);
          break;
        case "forecast-horizon":
          setForecastHorizon(value as ForecastHorizonApi);
          break;
        case "confidence-level":
          setConfidenceLevel(value as ConfidenceLevelApi);
          break;
        case "include-weekends":
          setIncludeWeekends(value as boolean);
          break;
        case "theme":
          setTheme(value as ThemeApi);
          break;
        case "default-landing-page":
          // Live mode: not yet exposed by API — no-op until backend supports it
          break;
      }
    } else {
      switch (settingId) {
        case "low-cash-threshold":
          setDemoLowCashThreshold(Number(value));
          break;
        case "overdue-invoice-alert":
          setDemoOverdueInvoiceAlert(value as boolean);
          break;
        case "variance-threshold":
          setDemoVarianceThreshold(Number(value));
          break;
        case "email-notifications":
          setDemoEmailNotifications(value as boolean);
          break;
        case "feed-notifications":
          setDemoFeedNotifications(value as boolean);
          break;
        case "forecast-horizon":
          setDemoForecastHorizon(value as string);
          break;
        case "confidence-level":
          setDemoConfidenceLevel(value as string);
          break;
        case "include-weekends":
          setDemoIncludeWeekends(value as boolean);
          break;
        case "theme":
          setDemoTheme(value as string);
          break;
        case "default-landing-page":
          setDemoDefaultLandingPage(value as string);
          break;
      }
    }
  };

  const handleSave = async () => {
    if (isLive) {
      await save();
    } else {
      setDemoIsSaving(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Defaults Saved", {
        description:
          "User preference defaults have been updated for all new users.",
      });
      setDemoIsSaving(false);
    }
  };

  const currentIsSaving = isLive ? isSaving : demoIsSaving;

  const renderSetting = (setting: Setting) => {
    switch (setting.type) {
      case "toggle":
        return (
          <div className="flex items-center justify-between">
            <Label htmlFor={setting.id} className="font-normal cursor-pointer">
              {setting.label}
            </Label>
            <Switch
              id={setting.id}
              checked={setting.value as boolean}
              onCheckedChange={(checked) =>
                handleSettingChange(setting.id, checked)
              }
            />
          </div>
        );

      case "amount":
        return (
          <div className="space-y-2">
            <Label htmlFor={setting.id}>{setting.label}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                £
              </span>
              <Input
                id={setting.id}
                type="number"
                value={setting.value as number}
                onChange={(e) =>
                  handleSettingChange(setting.id, e.target.value)
                }
                className="pl-7"
              />
            </div>
          </div>
        );

      case "percentage":
        return (
          <div className="space-y-2">
            <Label htmlFor={setting.id}>{setting.label}</Label>
            <div className="relative">
              <Input
                id={setting.id}
                type="number"
                value={setting.value as number}
                onChange={(e) =>
                  handleSettingChange(setting.id, e.target.value)
                }
                className="pr-7"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                %
              </span>
            </div>
          </div>
        );

      case "select":
        return (
          <div className="space-y-2">
            <Label htmlFor={setting.id}>{setting.label}</Label>
            <Select
              value={setting.value as string}
              onValueChange={(value) => handleSettingChange(setting.id, value)}
            >
              <SelectTrigger id={setting.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {setting.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {setting.helperText && (
              <p className="text-sm text-muted-foreground">
                {setting.helperText}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLive && isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {mode === "demo" && (
        <Alert>
          <AlertDescription>
            You're viewing demo data. Switch to Live Mode to manage real user
            preference defaults.
          </AlertDescription>
        </Alert>
      )}

      {isLive && error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {sections.map((section) => {
          const isCollapsed = collapsedSections.has(section.id);
          const enabledCount = getEnabledCount(section);
          const totalCount = section.settings.length;

          return (
            <Card key={section.id}>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div
                    className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-md -ml-2 -mt-2"
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <h3 className="font-semibold text-sm">{section.name}</h3>
                      <Badge variant="secondary" className="text-xs">
                        {enabledCount} of {totalCount}
                      </Badge>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="space-y-4 pl-2">
                      {section.settings.map((setting) => (
                        <div key={setting.id}>{renderSetting(setting)}</div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardContent className="flex items-start gap-3 pt-6">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium">Applies to New Users Only</p>
            <p className="text-sm text-muted-foreground">
              These defaults only apply to newly created user accounts. Existing
              users can change their preferences in their personal settings.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} size="lg" disabled={currentIsSaving}>
          {currentIsSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Default Preferences
        </Button>
      </div>
    </div>
  );
}
